'use client';

import React, { useState } from 'react';
import { Dialog, Search } from '../PulseUI';
import { useDebounce } from 'use-debounce';
import useGlobalSearch from 'src/hooks/globalSearch/useGlobalSearch';
import { GlobalEmptyState, GlobalSearchItem } from './components';
import { CircularProgress } from 'src/components';

interface GlobalSearchDialogProps {
  open: boolean;
  onClose: () => void;
}

const GlobalSearchDialog: React.FC<GlobalSearchDialogProps> = ({
  open,
  onClose,
}): React.ReactElement => {
  const [search, setSearch] = useState('');
  const [debouncedSearch] = useDebounce(search, 500);

  const { data: searchResults, isLoading } = useGlobalSearch(debouncedSearch);

  const handleClose = () => {
    setSearch('');
    onClose();
  };

  const handleResultClick = () => {
    handleClose();
  };

  return (
    <Dialog
      isOpen={open}
      onClose={handleClose}
      size="xl"
      topAligned
      className="h-auto rounded-xl shadow-2xl w-full max-w-xl"
      slotProps={{
        backDropClassName: 'bg-black/20 backdrop-blur-none',
        containerClassName: 'py-40',
      }}
    >
      <div className="flex flex-col gap-2 py-4">
        <div className="flex-shrink-0 px-4">
          <Search
            autoFocus
            value={search}
            onSearch={setSearch}
            variant="outlined"
            className="h-10 rounded-full border border-neutral-300"
            placeholder="Search"
            slotProps={{
              iconClassName: 'text-secondary',
            }}
          />
        </div>

        {/* Results Container */}
        {debouncedSearch && (searchResults?.length || isLoading) ? (
          <div className="min-h-52 max-h-[calc(100vh-24rem)] overflow-y-auto [&::-webkit-scrollbar]:hidden [-ms-overflow-style:none] [scrollbar-width:none]">
            {isLoading ? (
              <div className="min-h-52 flex items-center justify-center">
                <CircularProgress
                  size="sm"
                  color="neutral"
                  data-testid="loading-indicator"
                />
              </div>
            ) : searchResults?.length ? (
              <div className="flex flex-col gap-2">
                {searchResults.map((item: any, index: number) => (
                  <GlobalSearchItem
                    key={`${item.type}-${item.provider_id}-${index}`}
                    item={item}
                    onClick={handleResultClick}
                  />
                ))}
              </div>
            ) : (
              <div className="min-h-52 flex items-center justify-center text-center text-neutral-500">
                No results found
              </div>
            )}
          </div>
        ) : (
          <GlobalEmptyState />
        )}
      </div>
    </Dialog>
  );
};

export default GlobalSearchDialog;
